<template>
  <div v-if="visible">
    <div class="modal fade show d-block" tabindex="-1" role="dialog" aria-modal="true" @click.self="$emit('close')">
      <div class="modal-dialog modal-dialog-centered initial-downloads-modal-dialog" role="document">
        <div class="modal-content initial-downloads-modal-content">
          <div class="modal-header initial-downloads-modal-header">
            <h5 class="modal-title"><i class="fa fa-download text-warning me-2"></i>Descargas iniciales en curso</h5>
            <button type="button" class="btn-close" aria-label="Close" @click="$emit('close')"></button>
          </div>

          <div class="modal-body initial-downloads-modal-body">
            <p class="initial-downloads-title mb-1">Tu e.firma se registró correctamente</p>
            <p class="initial-downloads-subtitle mb-3">A partir de este momento iniciamos las descargas iniciales de CFDIs para tu RFC.</p>

            <div class="initial-downloads-info-row">
              <div class="initial-downloads-info-item">
                <span class="initial-downloads-info-label">RFC</span>
                <span class="initial-downloads-info-value">{{ rfc || '-' }}</span>
              </div>
              <div class="initial-downloads-info-item">
                <span class="initial-downloads-info-label">Estado</span>
                <span class="initial-downloads-info-value">{{ details.statusLabel }}</span>
              </div>
              <div class="initial-downloads-info-item">
                <span class="initial-downloads-info-label">Tipo</span>
                <span class="initial-downloads-info-value">{{ details.typeLabel }}</span>
              </div>
              <div class="initial-downloads-info-item">
                <span class="initial-downloads-info-label">Periodo</span>
                <span class="initial-downloads-info-value">{{ details.periodLabel }}</span>
              </div>
            </div>

            <div class="initial-downloads-warning">
              <i class="fa fa-info-circle"></i>
              <div>
                <strong>{{ details.statusLabel || 'Descarga en proceso' }}</strong>
                <p>{{ details.scheduleLabel || 'Mantén esta ventana abierta para seguir el avance en la tabla del Centro de Descargas.' }}</p>
              </div>
            </div>
          </div>

          <div class="modal-footer initial-downloads-modal-footer">
            <button type="button" class="btn btn-primary" @click="$emit('close')">Entendido</button>
          </div>
        </div>
      </div>
    </div>

    <div class="modal-backdrop fade show"></div>
  </div>
</template>

<script>
import dashboardService from '@/services/dashboard';

export default {
  name: 'InitialDownloadsModal',
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    rfc: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      loading: false,
      details: {
        statusLabel: 'Esperando',
        typeLabel: '-',
        periodLabel: this.currentMonthLabel(),
        scheduleLabel: 'La solicitud está pendiente y se ejecutará en cuanto haya disponibilidad.',
      },
    };
  },
  watch: {
    visible: {
      immediate: true,
      handler(value) {
        if (value) {
          this.loadInitialRequestHint();
          return;
        }

        this.resetDetails();
      },
    },
    rfc() {
      if (this.visible) {
        this.loadInitialRequestHint();
      }
    },
  },
  methods: {
    currentMonthLabel() {
      const formatted = new Intl.DateTimeFormat('es-MX', {
        month: 'long',
        year: 'numeric',
      }).format(new Date());

      return formatted.charAt(0).toUpperCase() + formatted.slice(1);
    },
    resetDetails() {
      this.loading = false;
      this.details = {
        statusLabel: 'Esperando',
        typeLabel: '-',
        periodLabel: this.currentMonthLabel(),
        scheduleLabel: 'La solicitud está pendiente y se ejecutará en cuanto haya disponibilidad.',
      };
    },
    formatStatusLabel(status) {
      const normalized = String(status || '').trim().toLowerCase();

      if (normalized === 'in_progress' || normalized === 'processing') return 'En proceso';
      if (normalized === 'downloading') return 'Descargando';
      if (normalized === 'requested' || normalized === 'pending') return 'Esperando';

      return 'Esperando';
    },
    formatTypeLabel(type) {
      const normalized = String(type || '').trim().toLowerCase();

      if (normalized === 'issued') return 'Emitidos';
      if (normalized === 'received') return 'Recibidos';

      return '-';
    },
    formatPeriodLabel(period) {
      const normalized = String(period || '').trim();
      if (!normalized) {
        return this.currentMonthLabel();
      }

      const monthMatch = normalized.match(/^(\d{4})-(\d{2})$/);
      if (monthMatch) {
        const year = Number(monthMatch[1]);
        const month = Number(monthMatch[2]);
        if (month >= 1 && month <= 12) {
          const formatted = new Intl.DateTimeFormat('es-MX', {
            month: 'long',
            year: 'numeric',
          }).format(new Date(year, month - 1, 1));

          return formatted.charAt(0).toUpperCase() + formatted.slice(1);
        }
      }

      const yearMatch = normalized.match(/^(\d{4})$/);
      if (yearMatch) {
        return `Año ${yearMatch[1]}`;
      }

      return normalized;
    },
    formatDateTimeLabel(value) {
      const raw = String(value || '').trim();
      if (!raw) {
        return '';
      }

      const normalized = raw.includes('T') ? raw : raw.replace(' ', 'T');
      const date = new Date(normalized);
      if (Number.isNaN(date.getTime())) {
        return '';
      }

      return date.toLocaleString('es-MX', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });
    },
    parseDateTimeValue(value) {
      const raw = String(value || '').trim();
      if (!raw) {
        return null;
      }

      const normalized = raw.includes('T') ? raw : raw.replace(' ', 'T');
      const parsedDate = new Date(normalized);

      if (Number.isNaN(parsedDate.getTime())) {
        return null;
      }

      return parsedDate;
    },
    buildPendingRequestTitle(item) {
      const status = String(item?.status || '').trim().toLowerCase();
      const normalizedStatus = status === 'downloading' ? 'in_progress' : status;
      const explicitNatureLabel = String(item?.nature_label || '').trim();
      const type = String(item?.type || '').trim().toLowerCase();
      const natureLabel = explicitNatureLabel || (type === 'issued' ? 'Emitidos' : 'Recibidos');
      const explicitPeriodLabel = String(item?.period_label || '').trim();
      const periodLabel = explicitPeriodLabel || this.formatPeriodLabel(item?.period || '');
      const effectiveDateRaw = item?.scheduled_date || item?.request_date || '';
      const effectiveDate = this.parseDateTimeValue(effectiveDateRaw);
      const dateLabel = this.formatDateTimeLabel(effectiveDateRaw);
      const isScheduledDateOverdue = Boolean(effectiveDate && effectiveDate.getTime() < Date.now());

      if (normalizedStatus === 'in_progress') {
        return {
          statusLabel: 'En proceso',
          typeLabel: natureLabel,
          periodLabel,
          scheduleLabel: dateLabel ? `Inicio estimado: ${dateLabel}` : 'La descarga está actualmente en ejecución.',
        };
      }

      return {
        statusLabel: 'Esperando',
        typeLabel: natureLabel,
        periodLabel,
        scheduleLabel: isScheduledDateOverdue
          ? 'La solicitud sigue esperando. Hay muchas peticiones de descarga o existe intermitencia en el SAT.'
          : dateLabel
            ? `Programada para: ${dateLabel}`
            : 'La solicitud está pendiente y se ejecutará en cuanto haya disponibilidad.',
      };
    },
    parseDateFromDdMmYyyy(value) {
      const raw = String(value || '').trim();
      const match = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
      if (!match) {
        return null;
      }

      const day = Number(match[1]);
      const month = Number(match[2]);
      const year = Number(match[3]);

      if (!day || !month || !year) {
        return null;
      }

      return new Date(year, month - 1, day);
    },
    formatMonthYearFromDate(date) {
      if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
        return 'Periodo no definido';
      }

      const formatted = new Intl.DateTimeFormat('es-MX', {
        month: 'long',
        year: 'numeric',
      }).format(date);

      return formatted.charAt(0).toUpperCase() + formatted.slice(1);
    },
    buildPendingHintFromGroups(groups) {
      const candidateGroups = [];

      if (Array.isArray(groups?.manual_today)) {
        candidateGroups.push(...groups.manual_today);
      }

      if (Array.isArray(groups?.daily_last_10_days)) {
        candidateGroups.push(...groups.daily_last_10_days);
      }

      if (groups?.initial_summary) {
        candidateGroups.push(groups.initial_summary);
      }

      if (!candidateGroups.length) {
        return null;
      }

      const statusPriority = {
        in_progress: 0,
        downloading: 0,
        requested: 1,
      };

      const pending = candidateGroups
        .map((item) => {
          const status = String(item?.status || '').trim().toLowerCase();
          if (!(status in statusPriority)) {
            return null;
          }

          return {
            raw: item,
            status,
            priority: statusPriority[status],
          };
        })
        .filter(Boolean)
        .sort((a, b) => {
          if (a.priority !== b.priority) {
            return a.priority - b.priority;
          }

          const aDate = String(a.raw?.requested_at_raw || '');
          const bDate = String(b.raw?.requested_at_raw || '');
          if (aDate === bDate) {
            return 0;
          }

          return aDate < bDate ? -1 : 1;
        });

      if (!pending.length) {
        return null;
      }

      const selected = pending[0].raw || {};
      const types = Array.isArray(selected?.types) ? selected.types.map((t) => String(t || '').toLowerCase()) : [];

      let natureLabel = 'Recibidos';
      if (types.includes('issued') && types.includes('received')) {
        natureLabel = 'Recibidos y Emitidos';
      } else if (types.includes('issued')) {
        natureLabel = 'Emitidos';
      }

      const rangeEndDate = this.parseDateFromDdMmYyyy(selected?.range_end);
      const rangeStartDate = this.parseDateFromDdMmYyyy(selected?.range_start);
      const periodDate = rangeEndDate || rangeStartDate || null;
      const periodLabel = this.formatMonthYearFromDate(periodDate);

      return {
        status: pending[0].status,
        nature_label: natureLabel,
        period_label: periodLabel,
        scheduled_date: selected?.scheduled_at_raw || selected?.requested_at_raw || '',
      };
    },
    applyRequestHint(hint) {
      const content = this.buildPendingRequestTitle(hint || {});
      this.details = {
        statusLabel: content?.statusLabel || 'Esperando',
        typeLabel: content?.typeLabel || '-',
        periodLabel: content?.periodLabel || this.currentMonthLabel(),
        scheduleLabel: content?.scheduleLabel || 'La solicitud está pendiente y se ejecutará en cuanto haya disponibilidad.',
      };
    },
    async loadInitialRequestHint() {
      const normalizedRfc = String(this.rfc || '').trim().toUpperCase();
      if (!normalizedRfc) {
        this.resetDetails();
        return;
      }

      this.loading = true;

      try {
        const response = await dashboardService.getDownloadCenterHistory(normalizedRfc, 1, 1, { skipLoading: true });
        const hint = response?.pending_request_hint || this.buildPendingHintFromGroups(response?.groups) || null;

        if (hint) {
          this.applyRequestHint(hint);
          return;
        }

        this.resetDetails();
      } catch (error) {
        this.resetDetails();
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.initial-downloads-modal-dialog {
  max-width: 560px;
}

.initial-downloads-modal-content {
  border-radius: 14px;
  overflow: hidden;
}

.initial-downloads-modal-header {
  border-bottom: 1px solid #e9ecef;
  padding: 16px 20px;
}

.initial-downloads-modal-body {
  padding: 18px 20px 14px;
}

.initial-downloads-title {
  font-size: 20px;
  font-weight: 700;
  color: #2f2f4a;
}

.initial-downloads-subtitle {
  font-size: 14px;
  color: #6c757d;
}

.initial-downloads-info-row {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  background: #f4f6fb;
  border-radius: 10px;
  padding: 12px;
  margin-bottom: 14px;
}

.initial-downloads-info-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.initial-downloads-info-label {
  font-size: 11px;
  font-weight: 700;
  color: #8a8da2;
  text-transform: uppercase;
  letter-spacing: .4px;
}

.initial-downloads-info-value {
  font-size: 15px;
  font-weight: 700;
  color: #2f3b59;
}

.initial-downloads-warning {
  display: flex;
  gap: 10px;
  border: 1px solid #ffd266;
  background: #fff8e5;
  border-radius: 10px;
  padding: 12px 14px;
  color: #7a5a00;
}

.initial-downloads-warning i {
  font-size: 18px;
  margin-top: 2px;
}

.initial-downloads-warning strong {
  display: block;
  font-size: 14px;
  margin-bottom: 2px;
}

.initial-downloads-warning p {
  margin: 0;
  font-size: 13px;
}

.initial-downloads-modal-footer {
  border-top: 1px solid #e9ecef;
  padding: 12px 20px 16px;
}

@media (max-width: 576px) {
  .initial-downloads-info-row {
    grid-template-columns: 1fr;
  }
}
</style>